import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import visionLogo from "@/assets/Logo_Black_Outline.png";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

const passwordSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PageState = "verifying" | "invalid" | "ready" | "success";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState<PageState>("verifying");
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [tokenError, setTokenError] = useState<string>("");
  // The account this link resets, as the server knows it. Shown on the form, because a browser can
  // still be signed in to a different account (VA-278).
  const [resetEmail, setResetEmail] = useState<string>("");

  // Get token from URL
  const token = searchParams.get("token");

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError("No reset token provided. Please request a new password reset link.");
        setPageState("invalid");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('verify-reset-token', {
          body: { token }
        });

        if (error || !data?.valid) {
          setTokenError(data?.error || "Invalid or expired reset link. Please request a new one.");
          setPageState("invalid");
          return;
        }

        if (typeof data?.email === "string") setResetEmail(data.email);
        setPageState("ready");
      } catch (err) {
        console.error("Token verification error:", err);
        setTokenError("Failed to verify reset link. Please try again.");
        setPageState("invalid");
      }
    };

    verifyToken();
  }, [token]);

  const validateForm = () => {
    try {
      passwordSchema.parse({ password, confirmPassword });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { password?: string; confirmPassword?: string } = {};
        error.errors.forEach((err) => {
          if (err.path[0] === "password") fieldErrors.password = err.message;
          if (err.path[0] === "confirmPassword") fieldErrors.confirmPassword = err.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('complete-password-reset', {
        body: { token, newPassword: password }
      });
      
      if (error || !data?.success) {
        toast({
          title: "Error",
          description: data?.error || "Failed to reset password. Please try again.",
          variant: "destructive",
        });
      } else {
        // Leave no one signed in. The reset is for the account in the link, but this browser can
        // still hold another account's session; the Sign In button below would then land the member
        // on that other account's dashboard. QA saw exactly that on VA-278: a reset for one account
        // opened qa-solo's dashboard, because qa-solo was still signed in there.
        await supabase.auth.signOut({ scope: "local" });
        setPageState("success");
        toast({
          title: "Password updated!",
          description: "Your password has been successfully reset.",
        });
      }
    } catch (err) {
      console.error("Password reset error:", err);
      toast({
        title: "Error",
        description: "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (pageState === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img src={visionLogo} alt="My Vision Assistant" className="h-12 object-contain" />
            </div>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <CheckCircle className="h-12 w-12 text-primary mx-auto" />
                <h2 className="text-xl font-semibold">Password Updated!</h2>
                <p className="text-muted-foreground">
                  Your password has been successfully reset.
                </p>
                <Button
                  onClick={() => navigate("/auth")}
                  variant="accent"
                  className="w-full"
                >
                  Sign In
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (pageState === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img src={visionLogo} alt="My Vision Assistant" className="h-12 object-contain" />
            </div>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <XCircle className="h-12 w-12 text-destructive mx-auto" />
                <h2 className="text-xl font-semibold">Invalid Reset Link</h2>
                <p className="text-muted-foreground">
                  {tokenError}
                </p>
                <Button
                  onClick={() => navigate("/auth")}
                  variant="accent"
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Verifying state
  if (pageState === "verifying") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Ready state - show password form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {/* Logo/Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src={visionLogo} alt="My Vision Assistant" className="h-12 object-contain" />
          </div>
          <p className="text-muted-foreground">A My Vision Agency Product</p>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>Set New Password</CardTitle>
            <CardDescription>
              {resetEmail ? <>Enter a new password for <span className="font-medium text-foreground">{resetEmail}</span></> : "Enter your new password below"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                variant="accent"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
